import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ImagePlus, Images, CreditCard, TrendingUp, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { galleryAPI, getImageUrl } from "../lib/api";
import UserLayout from "../components/UserLayout";
import { Button } from "../components/ui/button";

const DashboardPage = () => {
    const { user, refreshUser } = useAuth();
    const { t } = useLanguage();
    const [recentImages, setRecentImages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                await refreshUser();
                const response = await galleryAPI.getAll(0, 6);
                setRecentImages(response.data);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const stats = [
        {
            label: t('credits_remaining'),
            value: user?.credits || 0,
            icon: CreditCard,
            color: "text-yellow-600",
            bg: "bg-yellow-50"
        },
        {
            label: t('total_images'),
            value: recentImages.length,
            icon: Images,
            color: "text-blue-600",
            bg: "bg-blue-50"
        }
    ];

    return (
        <UserLayout>
            <div className="space-y-8" data-testid="user-dashboard">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">
                            {t('welcome')}, {user?.name}!
                        </h1>
                        <p className="text-gray-600 mt-1">
                            {t('dashboard_subtitle')}
                        </p>
                    </div>
                    <Link to="/generate">
                        <Button className="rounded-xl bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600" data-testid="generate-cta-btn">
                            <ImagePlus className="w-5 h-5 mr-2" />
                            {t('generate_new')}
                        </Button>
                    </Link>
                </div>

                {/* Stats */}
                <div className="grid md:grid-cols-2 gap-4">
                    {stats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center`}>
                                        <Icon className={`w-6 h-6 ${stat.color}`} />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">{stat.label}</p>
                                        <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Quick Actions */}
                <div className="grid md:grid-cols-3 gap-4">
                    <Link to="/generate" className="block">
                        <div className="bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl p-6 text-white hover:shadow-lg transition-all duration-200">
                            <ImagePlus className="w-10 h-10 mb-4" />
                            <h3 className="font-heading text-xl font-bold mb-2">{t('nav_generate')}</h3>
                            <p className="text-white/80">{t('dashboard_subtitle')}</p>
                        </div>
                    </Link>
                    <Link to="/gallery" className="block">
                        <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-200">
                            <Images className="w-10 h-10 mb-4 text-blue-500" />
                            <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">{t('gallery')}</h3>
                            <p className="text-gray-600">{t('gallery_subtitle')}</p>
                        </div>
                    </Link>
                    <Link to="/purchase" className="block">
                        <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-200">
                            <CreditCard className="w-10 h-10 mb-4 text-yellow-500" />
                            <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">{t('buy_credits')}</h3>
                            <p className="text-gray-600">{t('buy_credits_topup')}</p>
                        </div>
                    </Link>
                </div>

                {/* Recent Images */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-heading text-xl font-bold text-gray-900">{t('recent_images')}</h2>
                        <Link to="/gallery" className="text-red-500 hover:text-red-600 font-medium text-sm">
                            {t('view_all')}
                        </Link>
                    </div>
                    
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="aspect-square rounded-xl bg-gray-100 animate-pulse" />
                            ))}
                        </div>
                    ) : recentImages.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {recentImages.map((image, index) => (
                                <motion.div
                                    key={image.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="aspect-square rounded-xl overflow-hidden bg-gray-100"
                                >
                                    <img
                                        src={getImageUrl(image.thumbnail_url || image.image_url)}
                                        alt="Generated"
                                        className="w-full h-full object-cover"
                                    />
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">{t('no_images')}</p>
                            <Link to="/generate">
                                <Button className="mt-4 rounded-xl" data-testid="empty-generate-btn">
                                    {t('first_generate')}
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </UserLayout>
    );
};

export default DashboardPage;
